import React from "react";
import {
  Sprout, ArrowRight, ArrowLeft, Check,
  Cpu, Smartphone, WashingMachine, Boxes, LayoutGrid, LineChart, Target, Store, Bot, Gauge, Sparkles,
} from "lucide-react";
import { getVerticalPage, VERTICAL_PAGE_LIST } from "./vertical/verticalPages";
import { useSEO } from "../lib/useSEO";

const ICONS = { Cpu, Smartphone, WashingMachine, Boxes, LayoutGrid, LineChart, Target, Store, Bot, Gauge, Sparkles };
const Icon = ({ name, className }) => {
  const C = ICONS[name] || Boxes;
  return <C className={className} />;
};

export default function VerticalLanding({ vertical, onGetStarted, onLogin, onHome }) {
  const cfg = getVerticalPage(vertical);
  const login = onLogin || onGetStarted;

  useSEO({
    title: cfg ? cfg.seoTitle : "AssortIQ — AI Assortment Optimization",
    description: cfg ? cfg.seoDesc : "AI-driven assortment optimization for retail and agriculture.",
    canonical: cfg ? `https://getassortiq.com${cfg.path}` : "https://getassortiq.com/",
    jsonLd: cfg ? {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: `AssortIQ for ${cfg.name}`,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description: cfg.seoDesc,
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD", description: "7-day free trial" },
      url: `https://getassortiq.com${cfg.path}`,
    } : undefined,
  });

  if (!cfg) {
    if (typeof window !== "undefined") window.location.href = "/";
    return null;
  }

  return (
    <div className="min-h-screen bg-forest-900 text-white font-body antialiased overflow-x-hidden">
      {/* NAV */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-forest-900/80 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <button data-testid="vertical-home" onClick={onHome} className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-ai flex items-center justify-center"><Sprout className="h-5 w-5" strokeWidth={2.4} /></div>
            <span className="font-heading font-extrabold text-xl">Assort<span className="text-ai">IQ</span></span>
          </button>
          <div className="flex items-center gap-3">
            <button data-testid="vertical-back" onClick={onHome} className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-white">
              <ArrowLeft className="h-4 w-4" /> All industries
            </button>
            <a href="/tools" className="hidden sm:inline text-sm font-semibold text-white/70 hover:text-white">Free tools</a>
            <button data-testid="vertical-login" onClick={login} className="text-sm font-semibold text-white/80 hover:text-white">Log in</button>
            <button data-testid="vertical-nav-cta" onClick={onGetStarted} className="group flex items-center gap-1.5 rounded-full bg-ai px-4 py-2 text-sm font-bold hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
              Start free trial <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,94,0,0.16),transparent_55%)]" />
        <div className="relative mx-auto max-w-7xl px-6 pt-16 pb-12 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-ai/40 bg-ai/10 px-3 py-1 text-xs font-semibold text-ai uppercase tracking-wider">
              <Icon name={cfg.icon} className="h-3.5 w-3.5" /> {cfg.eyebrow}
            </div>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
              {cfg.h1a}<br /><span className="text-ai">{cfg.h1b}</span>
            </h1>
            <p className="mt-5 text-white/70 text-base sm:text-lg max-w-xl leading-relaxed">{cfg.subhead}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button data-testid="vertical-hero-cta" onClick={onGetStarted} className="group flex items-center gap-2 rounded-full bg-ai px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
                Start 7-day free trial <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <a href={`/demo/${cfg.demo}`} data-testid="vertical-demo-link" className="rounded-full border border-white/25 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-colors">
                See a live demo
              </a>
            </div>
            <p className="mt-4 text-xs text-white/50 flex items-center gap-2"><Check className="h-4 w-4 text-action-keep" /> {cfg.trialNote}</p>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-ai/20 blur-3xl rounded-full" />
            <img src={cfg.heroImg} alt={cfg.heroAlt} className="relative rounded-2xl border border-white/10 shadow-2xl w-full h-80 object-cover" />
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="mx-auto max-w-7xl px-6 py-12">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 sm:p-10">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">{cfg.problem.title}</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {cfg.problem.points.map((p, i) => (
              <div key={i} className="flex gap-3" data-testid={`vertical-problem-${i}`}>
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ai" />
                <p className="text-white/70 leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-white/[0.02] border-y border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold">One platform for the whole {cfg.name.toLowerCase()} assortment</h2>
            <p className="mt-3 text-white/60">Explainable AI decisions, demand forecasting and simulation — no data team required.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cfg.features.map((f) => (
              <div key={f.title} data-testid={`vertical-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-ai/40 hover:-translate-y-1 transition-all">
                <div className="h-11 w-11 rounded-xl bg-ai/15 text-ai flex items-center justify-center"><Icon name={f.icon} className="h-6 w-6" /></div>
                <h3 className="mt-4 font-heading text-lg font-bold">{f.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid sm:grid-cols-3 gap-6 text-center">
          {cfg.stats.map((s) => (
            <div key={s.l} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="font-heading text-2xl font-extrabold text-ai">{s.n}</div>
              <div className="mt-1 text-sm text-white/60">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* RESULTS */}
      {cfg.results && (
        <section className="mx-auto max-w-7xl px-6 py-8">
          <div className="rounded-3xl border border-ai/25 bg-gradient-to-br from-ai/[0.08] to-transparent p-8 sm:p-10">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider"><Sparkles className="h-3.5 w-3.5" /> Example results</div>
              <h2 className="mt-4 font-heading text-2xl sm:text-3xl font-extrabold">What optimizing your {cfg.name.toLowerCase()} assortment can look like</h2>
            </div>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="vertical-results">
              {cfg.results.stats.map((s) => (
                <div key={s.l} className="text-center sm:text-left" data-testid={`vertical-result-${s.l.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                  <div className="font-heading text-4xl sm:text-5xl font-extrabold text-ai">{s.n}</div>
                  <div className="mt-1 text-sm text-white/60">{s.l}</div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-[11px] text-white/40">{cfg.results.note}</p>
          </div>
        </section>
      )}

      {/* OTHER INDUSTRIES */}
      <section className="mx-auto max-w-7xl px-6 pb-8">
        <div className="text-xs font-bold uppercase tracking-wider text-white/40">AssortIQ also fits</div>
        <div className="mt-3 flex flex-wrap gap-2">
          {VERTICAL_PAGE_LIST.filter((v) => v.id !== cfg.id).map((v) => (
            <a key={v.id} href={v.path} className="rounded-full border border-white/15 px-4 py-1.5 text-sm font-semibold text-white/70 hover:bg-white/10 hover:text-white transition-colors">{v.name}</a>
          ))}
          <a href="/farmers" className="rounded-full border border-white/15 px-4 py-1.5 text-sm font-semibold text-white/70 hover:bg-white/10 hover:text-white transition-colors">Agriculture</a>
          <a href="/demo/grocery" className="rounded-full border border-white/15 px-4 py-1.5 text-sm font-semibold text-white/70 hover:bg-white/10 hover:text-white transition-colors">Grocery</a>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        <div className="relative overflow-hidden rounded-3xl border border-ai/30 bg-gradient-to-br from-ai/20 to-transparent p-10 sm:p-14 text-center">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,94,0,0.15),transparent_70%)]" />
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold">See it on your own {cfg.name.toLowerCase()} data</h2>
            <p className="mt-3 text-white/70 max-w-xl mx-auto">Import a CSV or connect your POS and get explainable recommendations in the first session. Free for 7 days.</p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <button data-testid="vertical-footer-cta" onClick={onGetStarted} className="inline-flex items-center gap-2 rounded-full bg-ai px-7 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
                Start 7-day free trial <ArrowRight className="h-4 w-4" />
              </button>
              <a href={`/demo/${cfg.demo}`} className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-colors">Try the live demo</a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <button onClick={onHome} className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-ai flex items-center justify-center"><Sprout className="h-4 w-4" strokeWidth={2.4} /></div>
            <span className="font-heading font-extrabold">Assort<span className="text-ai">IQ</span></span>
          </button>
          <div className="text-xs text-white/40">© {new Date().getFullYear()} AssortIQ. Built for grocery, retail, electronics, appliances, cellular & agriculture.</div>
        </div>
      </footer>
    </div>
  );
}
